// Mirrors backend constants in apps/autoleads/constants.py.
// User-facing prices are in euros. Credits are the consumption unit; the
// per-credit euro figures below are the nominal rate used for cost display.
import type { AutoLeadsCatalogTier } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"
import type { Locale } from "@/types/nango"

export const CREDIT_UNIT_CENTS = 10

// Per-lead backend cost (kept for the staff cost panel and any internal calc).
export const BASE_LEAD_CREDIT_COST = 8

// Per-plugin credit cost (mirror of PLUGIN_CREDIT_COSTS). Whole credits only.
export const PLUGIN_CREDIT_COSTS: Record<string, number> = {
  email: 0,
  geocode: 0,
  socials: 1,
  rating: 2,
  website_quality_score: 4,
  mailing_copy: 5,
  ad_signals: 5,
  ad_check: 3,
  decision_makers: 3,
  custom_query: 3,
  job_offers: 3,
  security_audit: 2,
  pappers_lookup: 1,
  // Free half of the company-registry pair: the lead is billed on pappers_lookup.
  societe_lookup: 0,
  // Covered by website_quality_score: the SEO plugin runs it as a second pass.
  geo: 0,
}

// Per-source credit surcharge (mirror of SOURCE_LEAD_CREDIT_SURCHARGE). Added
// to the base lead cost at intake: a pappers lead costs 8 + 2 = 10 credits.
// ad_detection's surcharge is carried by its auto-enabled ad_signals plugin.
export const SOURCE_LEAD_CREDIT_SURCHARGE: Record<string, number> = {
  pappers: 2,
  // Google SERP discovery pays for its own search + place lookups: 8 + 2 = 10.
  google_serp: 2,
  // Trade-show discovery runs extra LLM + web research per lead: 8 + 5 = 13.
  trade_shows: 5,
  // Business-event discovery (Sirene + BODACC + resolution): 8 + 2 = 10.
  watcher: 2,
  // Import campaigns enrich leads the user already owns: importing is free
  // (8 - 8 = 0), the user only pays for the enrichment plugins they enable.
  import: -8,
}

// Discount applied to the annual plan vs the monthly plan (mirror of
// ANNUAL_DISCOUNT_PCT). Drives the "-20%" badge on the annual tab; the
// serializer's annual_discount_pct is the source of truth in the app, this is
// the fallback for the public landing page (no subscription loaded).
export const ANNUAL_DISCOUNT_PCT = 20

// Fixed subscription tiers (mirror of TIER_SPECS). Prices are HT euros and
// always expressed per month. annualMonthlyEquivEur is the discounted
// monthly-equivalent billed yearly (round(monthly * 0.8)).
export type PricingTier = {
  tier: AutoLeadsCatalogTier
  label: string
  priceEur: number
  annualMonthlyEquivEur: number
  credits: number
}

export const TIERS: PricingTier[] = [
  {
    tier: "starter",
    label: "Starter",
    priceEur: 249,
    annualMonthlyEquivEur: 199,
    credits: 2500,
  },
  {
    tier: "growth",
    label: "Growth",
    priceEur: 499,
    annualMonthlyEquivEur: 399,
    credits: 5500,
  },
  {
    tier: "scale",
    label: "Scale",
    priceEur: 899,
    annualMonthlyEquivEur: 719,
    credits: 10800,
  },
]

export function tierByKey(
  tier: AutoLeadsCatalogTier | "",
): PricingTier | undefined {
  return TIERS.find((t) => t.tier === tier)
}

// The middle tier is highlighted as the recommended default across the
// marketing pricing block and the in-app plan picker.
export const POPULAR_TIER: AutoLeadsCatalogTier = "growth"

// Position of a tier in the catalogue ladder, used to compare two plans.
export function tierRank(tier: AutoLeadsCatalogTier): number {
  return TIERS.findIndex((t) => t.tier === tier)
}

// Qualitative feature bullets per tier (bilingual). The credit count and the
// leads estimate are already shown on the card, so they are not repeated here.
// A bullet carrying a `detail` is rendered with a dotted underline and opens a
// hover card with that copy (see `components/tier-feature-list.tsx`); the
// detail replaces what used to be a parenthetical in the label itself.
export type TierFeature = {
  label: string
  detail?: string
}

// What each tier adds on its own, named without pointing at another plan. The
// catalogue card can afford "Starter plan included" because the Starter card is
// right beside it; a negotiated offer stands alone, so it needs the same
// entitlements spelled out.
const TIER_OWN_FEATURES: Record<
  AutoLeadsCatalogTier,
  { FR: TierFeature[]; EN: TierFeature[] }
> = {
  starter: {
    FR: [
      { label: "Accès aux filtres clés" },
      { label: "Enrichissement" },
      { label: "Séquences email" },
      { label: "Support email" },
      {
        label: "Accompagnement à la mise en place",
        detail:
          "Un point de démarrage avec notre équipe : configuration de vos premiers filtres, choix des enrichissements adaptés à votre cible et branchement de vos séquences email, jusqu'au lancement de votre première campagne.",
      },
    ],
    EN: [
      { label: "Access to key filters" },
      { label: "Enrichment" },
      { label: "Email sequences" },
      { label: "Email support" },
      {
        label: "Guided setup",
        detail:
          "A kickoff session with our team: configuring your first filters, picking the enrichments that fit your target and connecting your email sequences, through to launching your first campaign.",
      },
    ],
  },
  growth: {
    FR: [{ label: "API (bientôt)" }, { label: "Support prioritaire" }],
    EN: [{ label: "API (coming soon)" }, { label: "Priority support" }],
  },
  scale: {
    FR: [
      {
        label: "Vente en marque blanche",
        detail:
          "Revendez les leads que vous générez à vos clients sous votre propre marque.",
      },
      {
        label: "Accompagnement mensuel",
        detail:
          "Un point en visio chaque mois avec notre équipe : revue de vos campagnes, ajustement du ciblage et des séquences, priorisation des prochaines niches à ouvrir.",
      },
    ],
    EN: [
      {
        label: "White-label reselling",
        detail:
          "Resell the leads you generate to your clients under your own brand.",
      },
      {
        label: "Monthly check-in",
        detail:
          "A video call every month with our team: campaign review, targeting and sequence tuning, and picking the next niches to open.",
      },
    ],
  },
}

// What the plan opens in teams and members, in words rather than as a number:
// the caps are only meaningful with what they allow inside them said in the
// same breath. Teams are unlimited on every plan; members per team follow
// TIER_MEMBER_LIMITS in `apps/autoleads/constants.py`. Shared, because the
// catalogue card and a negotiated offer state the same entitlement and must
// not drift apart.
const TEAM_FEATURE: Record<
  AutoLeadsCatalogTier,
  { FR: TierFeature; EN: TierFeature }
> = {
  starter: {
    FR: {
      label: "Équipes illimitées, 1 membre par équipe",
      detail:
        "Créez autant d'équipes que nécessaire, une par client, par marque ou par niche. Chaque équipe a sa propre offre, ses propres crédits et ses propres campagnes, avec un seul accès à la plateforme.",
    },
    EN: {
      label: "Unlimited teams, 1 member per team",
      detail:
        "Create as many teams as you need, one per client, brand or niche. Each team has its own plan, its own credits and its own campaigns, with a single login to the platform.",
    },
  },
  growth: {
    FR: {
      label: "Équipes illimitées, jusqu'à 5 membres par équipe",
      detail:
        "Créez autant d'équipes que nécessaire et invitez jusqu'à 5 collaborateurs dans chacune, chacun avec son propre accès. Chaque équipe a sa propre offre, ses propres crédits et ses propres campagnes.",
    },
    EN: {
      label: "Unlimited teams, up to 5 members per team",
      detail:
        "Create as many teams as you need and invite up to 5 people into each, each with their own login. Each team has its own plan, its own credits and its own campaigns.",
    },
  },
  scale: {
    FR: {
      label: "Équipes illimitées, membres illimités",
      detail:
        "Créez autant d'équipes que nécessaire, une par client, par marque ou par niche, avec un nombre illimité de membres dans chacune. Chaque équipe a sa propre offre, ses propres crédits et ses propres campagnes.",
    },
    EN: {
      label: "Unlimited teams, unlimited members",
      detail:
        "Create as many teams as you need, one per client, brand or niche, with unlimited members in each. Each team has its own plan, its own credits and its own campaigns.",
    },
  },
}

// What only makes sense on a price list: the cumulative pointer at the tier
// below, the relative volume claim, and the team entitlement the card states in words.
const TIER_CARD_LEAD_IN: Record<
  AutoLeadsCatalogTier,
  { FR: TierFeature[]; EN: TierFeature[] }
> = {
  starter: { FR: [], EN: [] },
  growth: {
    FR: [
      { label: "Plan Starter inclus" },
      { label: "Plus de volume" },
      TEAM_FEATURE.growth.FR,
    ],
    EN: [
      { label: "Starter plan included" },
      { label: "More volume" },
      TEAM_FEATURE.growth.EN,
    ],
  },
  scale: {
    FR: [
      { label: "Plan Growth inclus" },
      { label: "Volume élevé" },
      TEAM_FEATURE.scale.FR,
    ],
    EN: [
      { label: "Growth plan included" },
      { label: "High volume" },
      TEAM_FEATURE.scale.EN,
    ],
  },
}

// The catalogue cards, unchanged: each tier still leads with what it inherits.
export const TIER_FEATURES: Record<
  AutoLeadsCatalogTier,
  { FR: TierFeature[]; EN: TierFeature[] }
> = {
  starter: {
    FR: [...TIER_CARD_LEAD_IN.starter.FR, ...TIER_OWN_FEATURES.starter.FR],
    EN: [...TIER_CARD_LEAD_IN.starter.EN, ...TIER_OWN_FEATURES.starter.EN],
  },
  growth: {
    FR: [...TIER_CARD_LEAD_IN.growth.FR, ...TIER_OWN_FEATURES.growth.FR],
    EN: [...TIER_CARD_LEAD_IN.growth.EN, ...TIER_OWN_FEATURES.growth.EN],
  },
  scale: {
    FR: [...TIER_CARD_LEAD_IN.scale.FR, ...TIER_OWN_FEATURES.scale.FR],
    EN: [...TIER_CARD_LEAD_IN.scale.EN, ...TIER_OWN_FEATURES.scale.EN],
  },
}

// Every entitlement a tier grants, said in full: its own and everything the
// tiers below it hand down. What a negotiated offer is described with, since
// its holder has no catalogue in front of them to resolve "Growth included".
export function flattenedTierFeatures(
  tier: AutoLeadsCatalogTier,
  locale: Locale,
): TierFeature[] {
  const ladder: AutoLeadsCatalogTier[] = ["starter", "growth", "scale"]
  return [
    TEAM_FEATURE[tier][locale],
    ...ladder
      .slice(0, ladder.indexOf(tier) + 1)
      .flatMap((step) => TIER_OWN_FEATURES[step][locale]),
  ]
}

// In-app "renewal soon" admonition thresholds. Annual plans get a longer lead
// time (a full year renewal is a bigger charge) than monthly plans.
export const RENEWAL_SOON_THRESHOLD_MONTHLY_DAYS = 5
export const RENEWAL_SOON_THRESHOLD_ANNUAL_DAYS = 14

export type CalcOptions = {
  websiteQuality: boolean
  ratings: boolean
  paidAds: boolean
  adCheck: boolean
  mailing: boolean
  socials: boolean
  jobOffers: boolean
  securityAudit: boolean
  pappersLookup: boolean
  decisionMakers: boolean
  customQuery: boolean
  requireEmail: boolean
  requirePhone: boolean
}

export const CALC_DEFAULTS: CalcOptions = {
  websiteQuality: true,
  ratings: true,
  paidAds: false,
  adCheck: false,
  mailing: false,
  socials: false,
  jobOffers: false,
  securityAudit: false,
  pappersLookup: false,
  decisionMakers: false,
  customQuery: false,
  requireEmail: false,
  requirePhone: false,
}

function activePluginSlugs(opts: CalcOptions): string[] {
  const slugs: string[] = ["email"]
  if (opts.ratings) slugs.push("rating")
  if (opts.websiteQuality) slugs.push("website_quality_score")
  if (opts.paidAds) slugs.push("ad_signals")
  if (opts.adCheck) slugs.push("ad_check")
  if (opts.mailing) slugs.push("mailing_copy")
  if (opts.socials) slugs.push("socials")
  if (opts.jobOffers) slugs.push("job_offers")
  if (opts.securityAudit) slugs.push("security_audit")
  if (opts.pappersLookup) slugs.push("pappers_lookup")
  if (opts.decisionMakers) slugs.push("decision_makers")
  if (opts.customQuery) slugs.push("custom_query")
  return slugs
}

export function leadCreditCost(opts: CalcOptions): number {
  const pluginCredits = activePluginSlugs(opts).reduce(
    (acc, slug) => acc + (PLUGIN_CREDIT_COSTS[slug] ?? 0),
    0,
  )
  return BASE_LEAD_CREDIT_COST + pluginCredits
}

// Floor per-lead credit cost for a discovery source (base + any source-only
// cost, no enrichment plugins). The ad_detection source carries the ad-proof
// cost that used to live in the paid-ads plugin.
export function sourceCreditCost(source: string): number {
  return (
    BASE_LEAD_CREDIT_COST +
    (source === "ad_detection" ? (PLUGIN_CREDIT_COSTS.ad_signals ?? 0) : 0) +
    (SOURCE_LEAD_CREDIT_SURCHARGE[source] ?? 0)
  )
}

// Leads obtainable per month from a monthly credit grant at the chosen plugin set.
export function monthlyLeadsFromCredits(
  credits: number,
  opts: CalcOptions,
): number {
  const perLeadCredits = leadCreditCost(opts)
  if (perLeadCredits <= 0) return 0
  return Math.floor(credits / perLeadCredits)
}

export function eurosPerLead(
  priceEur: number,
  credits: number,
  opts: CalcOptions,
): number {
  const leads = monthlyLeadsFromCredits(credits, opts)
  if (leads <= 0) return 0
  return priceEur / leads
}

// The app's locale is "FR" | "EN"; Intl wants a BCP-47 tag.
const LOCALE_TAG: Record<Locale, string> = { FR: "fr-FR", EN: "en-US" }

// Locale defaults to FR so the ~30 existing call sites keep their behaviour.
// Surfaces that have a locale to hand should pass it: an English build showing
// "0,091 €" reads as a bug to the person looking at it.
export function formatCredits(value: number, locale: Locale = "FR"): string {
  return value.toLocaleString(LOCALE_TAG[locale])
}

export function formatEuros(
  value: number,
  fractionDigits: number = 2,
  locale: Locale = "FR",
): string {
  return value.toLocaleString(LOCALE_TAG[locale], {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

// Accepts an in-progress credit amount: empty or whole digits only.
const CREDIT_INPUT_PATTERN = /^\d*$/

export function isCreditInput(raw: string): boolean {
  return CREDIT_INPUT_PATTERN.test(raw)
}

// Accepts an in-progress euro amount: whole digits plus up to two decimals,
// with either separator, so a French operator can type "12,50" as they say it.
const EURO_INPUT_PATTERN = /^\d*(?:[.,]\d{0,2})?$/

export function isEuroAmountInput(raw: string): boolean {
  return EURO_INPUT_PATTERN.test(raw)
}

// The decimal comma is what gets typed here; Number() only reads a point.
export function parseEuroAmount(raw: string): number {
  const parsed = Number(raw.replace(",", "."))
  return Number.isFinite(parsed) ? parsed : 0
}

// A billed price shown without decimals always rounds up. Rounding to nearest
// would advertise less than Stripe charges: a linear custom plan lands on
// fractions of a euro (8 000 credits at Growth's rate is 725,82 EUR), and the
// figure a customer reads must never be under the one on their invoice.
export function formatEurosCeil(value: number, locale: Locale = "FR"): string {
  return formatEuros(Math.ceil(value), 0, locale)
}

// A discount shown without decimals rounds DOWN, the mirror of
// `formatEurosCeil`: promising 101 EUR off when Stripe takes 100,50 EUR off is
// the same defect in the customer's favour.
export function formatEurosFloor(value: number, locale: Locale = "FR"): string {
  return formatEuros(Math.floor(value), 0, locale)
}
